export const CHECKOUT_URL = 'https://cbct-en.impultienda.ar/checkout/';

export const images = {
  mockup: 'https://cdn.magicpatterns.com/uploads/uFKzoRWLLwJNmWbMAQxMc4/CBCT-EN.png',
  author: 'https://cdn.magicpatterns.com/uploads/hp5BPjocirFMUaFh71apiw/MICHAEL_CARTER_-_IMPLANTES.png',
};

export const beforeItems = [
  'Opening a CBCT and not knowing where to start',
  'Jumping between different anatomical structures',
  'Worrying that you may have overlooked an important variable',
  'Spending too much time reviewing notes before planning an implant case',
  'Trying to remember everything you’ve learned about anatomy',
  'Making your review process different for every patient',
];

export const afterItems = [
  'Following the same 7-step CBCT review sequence',
  'Knowing what to examine at each stage',
  'Documenting the relevant anatomical findings',
  'Identifying areas that require additional clinical evaluation',
  'Moving from CBCT review to implant planning with a more organized process',
  'Having a repeatable reference you can use case after case',
];

export const reviewSteps = [
  { title: 'Orientation', text: 'Set the reference and analyze the scan correctly.' },
  { title: 'Bone Assessment', text: 'Evaluate bone quality and general conditions.' },
  { title: 'Anatomical Structures', text: 'Identify critical anatomical structures.' },
  { title: 'Available Dimensions', text: 'Measure height, width and ridge dimensions.' },
  { title: 'Risk Areas', text: 'Assess areas that require special attention.' },
  { title: 'Implant Position', text: 'Define position, axis and safety margins.' },
  { title: 'Final Planning Review', text: 'Confirm findings and pending questions.' },
];

export const volumes = [
  {
    number: 'I',
    title: 'Start With the Case — Not the CBCT',
    intro:
      'Before analyzing a single slice, you need to understand what you’re actually trying to determine. I’ll walk you through the initial questions that establish the purpose of the CBCT review and help you organize the case before getting lost in the images.',
    listLabel: 'You’ll learn how to:',
    items: [
      'Define the clinical question',
      'Establish what information you need',
      'Identify relevant patient and treatment factors',
      'Separate essential findings from secondary information',
      'Prepare your CBCT review before opening the images',
    ],
    closing: '',
  },
  {
    number: 'II',
    title: 'The 7-Step CBCT Review Sequence',
    intro:
      'This is the core of the system. Instead of jumping randomly through the scan, you’ll follow a structured sequence designed to help you review the case in the same order every time.',
    listLabel: 'You’ll work through:',
    items: [
      'Step 1 — Orientation',
      'Step 2 — Bone Assessment',
      'Step 3 — Anatomical Structures',
      'Step 4 — Available Dimensions',
      'Step 5 — Risk Areas',
      'Step 6 — Implant Position',
      'Step 7 — Final Planning Review',
    ],
    closing: 'The goal is simple: know what to look for before you start looking.',
  },
  {
    number: 'III',
    title: 'Anatomy You Need to Identify Before Planning an Implant',
    intro:
      'You don’t need another giant anatomy textbook. You need a practical reference that helps you identify the structures that may be relevant when evaluating an implant site.',
    listLabel: 'Structured review guides for:',
    items: [
      'Relevant anatomical structures',
      'Critical areas',
      'Bone availability',
      'Ridge dimensions',
      'Important anatomical relationships',
      'Areas requiring additional evaluation',
      'Common points of attention during CBCT review',
    ],
    closing: '',
  },
  {
    number: 'IV',
    title: 'Measure Before You Plan',
    intro:
      'Once you’ve identified the relevant anatomy, the next question is: what information do you actually need to document before planning? This volume gives you a structured framework for recording the variables that matter.',
    listLabel: 'You’ll find:',
    items: [
      'Measurement worksheets',
      'Height assessment',
      'Width assessment',
      'Ridge evaluation',
      'Safety-margin considerations',
      'Implant position notes',
      'Planning documentation templates',
      'Case review tables',
    ],
    closing: 'Instead of simply looking at the CBCT… you’ll know what information you’re looking for.',
  },
  {
    number: 'V',
    title: 'The Implant Planning Decision Map',
    intro:
      'Now we connect your CBCT findings with the next stage of clinical planning, through a visual decision framework.',
    listLabel: 'Covering:',
    items: [
      'Site evaluation',
      'Implant position',
      'Implant axis',
      'Available bone',
      'Anatomical considerations',
      'Prosthetically relevant position',
      'Safety considerations',
      'Questions that should be resolved before surgery',
    ],
    closing:
      'The purpose is not to give you an automatic answer for every patient. It’s to give you a structured way to think through the variables before making a clinical decision.',
  },
  {
    number: 'VI',
    title: 'The Final CBCT Case Audit',
    intro: 'Before you move from imaging to treatment planning, use this final review.',
    listLabel: 'You’ll systematically check:',
    items: [
      'What have I confirmed?',
      'What information is still missing?',
      'What anatomical structures require attention?',
      'What risks need further assessment?',
      'Is the proposed implant position compatible with the information reviewed?',
      'What should be discussed, modified, investigated, or referred?',
    ],
    closing: 'This becomes your final checkpoint.',
  },
];

export const extras = [
  {
    icon: 'map',
    title: 'The 7-Step CBCT Master Map',
    text: 'A one-page visual overview of the entire system. Keep it beside your workstation as your quick-reference guide.',
  },
  {
    icon: 'checklist',
    title: 'CBCT Review Checklists',
    text: 'Structured checklists to help you standardize the way you review implant-related CBCT information.',
  },
  {
    icon: 'ruler',
    title: 'Implant Site Measurement Sheets',
    text: 'Templates for documenting the key dimensions and observations you identify during your review.',
  },
  {
    icon: 'alert',
    title: 'Anatomical Risk Review Sheets',
    text: 'A structured way to flag areas that require closer evaluation before moving forward.',
  },
  {
    icon: 'worksheet',
    title: 'Implant Planning Worksheets',
    text: 'Turn your CBCT observations into organized planning notes instead of scattered annotations.',
  },
] as const;

export const bonuses = [
  {
    number: 1,
    title: '25 CBCT Decision Cases',
    value: 37,
    image: 'https://cdn.magicpatterns.com/uploads/pGppWdrEv83gifsxMzLs2p/BONUS_1_CBCT-EN.png',
    text: 'Practice the framework with 25 educational case scenarios. For each case, you’ll work through questions such as:',
    items: [
      'What would you review first?',
      'Which anatomical structures deserve attention?',
      'What measurements would you document?',
      'What additional questions should be answered before planning?',
    ],
    closing: 'A practical way to turn the framework into deliberate practice.',
  },
  {
    number: 2,
    title: 'CBCT 10-Minute Quick Review™',
    value: 27,
    image: 'https://cdn.magicpatterns.com/uploads/vxvxYBrWNwX9VRkW9dVmNF/BONUS_2_CBCT-EN.png',
    text: 'A condensed version of the full system for those moments when you need a fast, structured review.',
    items: ['One page', 'Seven checkpoints', 'A clear sequence'],
    closing: 'Keep it next to your computer and use it as your rapid-reference sheet.',
  },
  {
    number: 3,
    title: 'The CBCT Red Flag Atlas™',
    value: 37,
    image: 'https://cdn.magicpatterns.com/uploads/1tAMk9SaK9Le5uMZCzHotV/BONUS_3_CBCT-EN.png',
    text: 'A visual collection of common findings and situations that should trigger a closer review before proceeding with implant planning. Organized into:',
    items: ['Anatomy', 'Bone', 'Dimensions', 'Risk areas', 'Position', 'Planning'],
    closing:
      'Designed as an educational reference — not as a substitute for clinical judgment, specialist assessment, or applicable standards of care.',
  },
  {
    number: 4,
    title: 'Implant Planning Case Sheet',
    value: 19,
    image: 'https://cdn.magicpatterns.com/uploads/5YcC2K48KxTZBuEgkD8Anv/BONUS_4_CBCT-EN.png',
    text: 'A professional case-planning worksheet designed to help you organize:',
    items: [
      'Patient information',
      'Site information',
      'CBCT observations',
      'Measurements',
      'Anatomical considerations',
      'Implant planning notes',
      'Questions requiring further evaluation',
    ],
    closing: '',
  },
  {
    number: 5,
    title: '30 CBCT Review Checklists',
    value: 27,
    image: 'https://cdn.magicpatterns.com/uploads/2ggUx84BnoTwmuQJk5fcXx/BONUS_5_CBCT-EN.png',
    text: 'A collection of printable checklists covering different stages of the CBCT review and implant-planning workflow.',
    items: [],
    closing: 'Use them digitally or print them for your clinical reference system.',
  },
];

export const offerIncludes = [
  { label: 'CBCT Decision Map™ — 6 Premium Volumes', value: 97 },
  { label: '5 Exclusive Bonuses', value: 147 },
];

export const offerPerks = [
  'One-time payment',
  'Lifetime access',
  'No monthly subscription',
  'Instant digital access',
  '7-Day Guarantee',
];

export const guaranteeItems = [
  'Explore all 6 volumes',
  'Review the checklists',
  'Use the CBCT Master Map',
  'Test the planning worksheets',
  'Work through the educational cases',
  'Review the bonus materials',
];

export const testimonials = [
  {
    name: 'Dr. Daniel M.',
    country: 'United States',
    headline: 'I finally had a sequence to follow.',
    quote:
      'I used to jump between different areas of the CBCT depending on the case. Having a defined sequence made my review process much more organized.',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/e8b061eb-c316-46a8-843d-6d2dc3103a11.jpg',
  },
  {
    name: 'Dr. Laura P.',
    country: 'Colombia',
    headline: 'The checklist is what I use the most.',
    quote:
      'The biggest difference for me was having the review points in front of me instead of trying to remember everything.',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/4b156ec8-57c5-4f38-a0fe-3f296b794024.jpg',
  },
  {
    name: 'Dr. Andrés R.',
    country: 'Mexico',
    headline: 'It changed how I prepare for implant cases.',
    quote:
      'I wasn’t looking for another long course. I wanted something practical that I could actually use while reviewing a case. That’s exactly what this gave me.',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/c610c6d6-9e1d-4821-a3be-b47953b99848.jpg',
  },
  {
    name: 'Dr. Michael T.',
    country: 'Spain',
    headline: 'Much easier to follow than scattered notes.',
    quote:
      'I had information from different courses and books everywhere. The Decision Map gave me one structured workflow.',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/62db9aec-8c04-4072-84cf-8e38130feb60.jpg',
  },
  {
    name: 'Dr. Sarah K.',
    country: 'Chile',
    headline: 'It feels like a clinical reference system.',
    quote:
      'The combination of the map, worksheets and checklists is what makes it useful. I can actually return to it case after case.',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/40747ea1-ef14-4747-b45e-e4fbed1c28f2.jpg',
  },
];

export const authorQuestions = [
  'What should I look at first?',
  'What should I look at next?',
  'Which findings matter for this case?',
  'What should I document?',
  'What questions still need to be answered before planning?',
];

export const authorQuotes = [
  'Here’s where I start.',
  'Here’s what I review next.',
  'Here’s what I need to document.',
  'And here’s what I need to clarify before moving forward.',
];

export const faqs = [
  {
    q: 'How will I receive the material?',
    a: 'Immediately after completing your purchase, you’ll receive access instructions by email. The 6 volumes and 5 bonuses are provided digitally so you can access them from your computer, tablet, or other compatible device.',
  },
  {
    q: 'Is this a course?',
    a: 'No. The CBCT Decision Map™ is designed as a practical reference and decision framework, not a traditional multi-hour video course. You’ll receive 6 structured volumes, visual maps, worksheets, checklists, educational cases and planning resources.',
  },
  {
    q: 'Do I need to be an implant specialist?',
    a: 'The material is designed for dental professionals who want a more structured approach to reviewing CBCT information in the context of implant-related case planning. Your appropriate use of the material depends on your education, training, scope of practice, and applicable regulations.',
  },
  {
    q: 'Do I need previous CBCT experience?',
    a: 'Basic familiarity with dental CBCT imaging is helpful. The system is designed to organize the review process rather than replace foundational education in radiology, anatomy, implantology, or clinical diagnosis.',
  },
  {
    q: 'Can I use the checklists digitally?',
    a: 'Yes. The resources are designed to be used digitally and/or printed for personal clinical reference.',
  },
  {
    q: 'Is this a one-time payment?',
    a: 'Yes. You pay $27 USD once. There is no monthly subscription. Your local currency is converted automatically at checkout.',
  },
  {
    q: 'Do I get lifetime access?',
    a: 'Yes. Your purchase includes lifetime access to the digital materials included with the offer.',
  },
  {
    q: 'Is the CBCT Decision Map™ a substitute for clinical judgment?',
    a: 'No. It is an educational and organizational tool. It does not provide automated diagnoses or treatment decisions and should not be used as a substitute for professional judgment, appropriate diagnostic evaluation, specialist referral, or applicable standards of care.',
  },
  {
    q: 'What happens if I don’t find it useful?',
    a: 'You have a 7-day guarantee. Review the material and decide whether it fits your workflow. If it isn’t right for you, contact us within the first 7 days for a full refund.',
  },
];

export const finalSummary = [
  '6 Premium Volumes',
  '5 Exclusive Bonuses',
  '40+ Review Tools & Checklists',
  '25 Educational Case Scenarios',
  '7-Step CBCT Review System',
];

export const recentBuyers = [
  { name: 'Valentina G.', country: 'Colombia' },
  { name: 'Carlos M.', country: 'Mexico' },
  { name: 'Lucía F.', country: 'Argentina' },
  { name: 'Javier R.', country: 'Spain' },
  { name: 'Camila S.', country: 'Chile' },
  { name: 'Diego A.', country: 'Peru' },
  { name: 'Mariana P.', country: 'Ecuador' },
  { name: 'Andrés V.', country: 'Uruguay' },
  { name: 'Sofía L.', country: 'Costa Rica' },
  { name: 'Mateo H.', country: 'Dominican Republic' },
  { name: 'Isabella C.', country: 'Panama' },
  { name: 'Alejandro T.', country: 'Venezuela' },
  { name: 'Gabriela N.', country: 'Guatemala' },
  { name: 'Fernando B.', country: 'Bolivia' },
  { name: 'Daniela O.', country: 'Paraguay' },
];
